import gql from 'graphql-tag';
import * as fragments from 'customer/components/policy/policy.query.fragments';
import { OFFER_FRAGMENT } from 'core/fragments/offer';
import { BRANCH_PLUS_POLICY_FRAGMENT } from 'core/fragments/branch-plus-policy';

export const GET_ACCOUNT = gql`
  query ($id: ID!) {
    account: getAccount(id: $id) {
      id
      fname
      lname
      created
      mailingAddress {
        address
        address2
        city
        state
        zip
      }
      phoneNumber
      email
      inviter {
        id
        firstName
        lastName
      }
      frontTagId
      startedUploadingInventoryImages
      memberAffinityCode
    }
  }
`;

export const GET_ACCOUNT_BRANCH_PLUS_POLICY = gql`
  query ($accountId: ID!) {
    account: getAccount(id: $accountId) {
      id
      fname
      lname
      created
      mailingAddress {
        address
        address2
        city
        state
        zip
      }
      phoneNumber
      email
      additionalPhoneNumbers {
        phoneNumber
        note
        canText
      }
      inviter {
        id
        firstName
        lastName
      }
      frontTagId
      startedUploadingInventoryImages
      memberAffinityCode
    }
    branchPlusPolicies: getAccountBranchPlusPolicies(accountId: $accountId) {
      ...BranchPlusPolicyFragment
    }
  }
  ${BRANCH_PLUS_POLICY_FRAGMENT}
`;

export const GET_FULL_ACCOUNT = gql`
  query ($id: ID!) {
    account: getAccount(id: $id) {
      id
      fname
      lname
      created
      mailingAddress {
        address
        address2
        city
        state
        zip
      }
      phoneNumber
      email
      additionalPhoneNumbers {
        phoneNumber
        note
        canText
      }
      inviter {
        id
        firstName
        lastName
      }
      frontTagId
      startedUploadingInventoryImages
      memberAffinityCode
      policies {
        webUserId
        id
        offerId
        isAgentSold
        policyType
        source
        importedData {
          conversionMetadata {
            policyNumber
          }
        }
        state
        effectiveDate
        branchPlusLinkedOfferId
        endDate
        fullTermPolicyEndDate
        premium
        surplusContribution
        fromStaff
        stripeCustomerId
        paymentMethod
        salesRep
        bindRep
        cancelled
        nonRenewed
        fullTermCancel
        homeInspectionRepairsRequested
        offer {
          ...OfferFragment
        }
        versionHistory {
          updatedDateTime
        }
        segments {
          segmentId
          startDate
          drivers {
            id
            isPrimary
            insuranceScore {
              autoTotal
              homeTotal
            }
          }
          cars {
            VIN
          }
          global {
            affinity
            affinityGroupStem
            continuousAutoCoverageStartDate
            rateControlDate
          }
          autoCoverage {
            policyLimitBIPD
          }
        }
        overrides {
          canEditUneditablePropertyDetails
          allowExcludingDrivers
          overridesForFeFormValidation {
            skipBogusLoanNumberCheck
            skipUmbrellaBIPDValidation
          }
          overridesForUW {
            code
            overridingAgent
            type
          }
          rc1 {
            itemsChanged
          }
        }
        policyDetails {
          autoCoverage {
            policyLimitBIPD
            policyLimitUMBI
            policyLimitUIMBI
            policyLimitPIP
            policyLimitUMPD
            policyLimitMedicalPayments
            policyLimitLPD
            petInjury
            coverageEducatorAdvantage
            coverageOffPremisesExpanded
            policyLimitPIPME
            policyLimitPIPWL
            policyLimitPIPACR
            policyLimitPPI
            policyLimitIncomeLoss
            policyLimitAccidentalDeath
            policyLimitFuneralBenefits
            policyLimitExtraMedBenefits
            policyLimitTortOption
            policyLimitComboFBP
            policyLimitGuestPIP
            policyLimitNoFaultPIP
            uimAddedOn
            skipPDValidation
          }
          homeCoverage {
            coverageA
            coverageEducatorAdvantage
            coverageOffPremisesExpanded
            minimumDeductibleValue
            minimumWindHailDeductible
            minimumWindHailDeductibleValue
            minimumHurricaneDeductible
            minimumHurricaneDeductibleValue
            interestedInSeparateWindHail
            externalPolicyId
          }
          includeUmbrella
          includeEarthquake
          includeFlood
          earthquakeCoverage {
            policyId
          }
          floodCoverage {
            policyId
          }
          drivers {
            id
            firstName
            lastName
            excludeDriver
            waivedPIPWL
            pipEligible
            pipOptOut
            pipHasQHC
            isPrimary
            insuranceScore {
              autoTotal
              homeTotal
            }
          }
          nonDrivers {
            id
            firstName
            lastName
          }
          people {
            id
            firstName
            lastName
          }
          cars {
            VIN
          }
          global {
            rateControlDate
          }
        }
        fees {
          amount
        }
      }
    }
    documents: getDocs(accountId: $id) {
      regular {
        path
        lastModified
      }
      internal {
        path
        lastModified
        displayName
        policyId
      }
    }
    unsignedApplications: getUnsignedApplications(accountId: $id) {
      policyId
      applicationUrl
    }
    unsignedBixConversions: getUnsignedBixConversions(accountId: $id) {
      policyId
    }
    pledges: getAccountPledges(accountId: $id) {
      pledges {
        name
        pledgedTimeStamp
      }
    }
    myCommunity: getAccountCommunity(accountId: $id) {
      community {
        name
        communityTimeStamp
      }
    }
    tasks: getOnboardingTasks(accountId: $id) {
      ... on OnboardingTasksResponse {
        success
        tasks {
          taskId
          taskName
          data
          completedDate
          policyId
          completedBy
          approvalDate
          approvedBy
          rejections {
            timestamp
            rejectedByUserId
            reason
            timestamp
          }
        }
      }
      ... on OnboardingTasksError {
        success
        error
      }
    }
    branchPlusPolicies: getAccountBranchPlusPolicies(accountId: $id) {
      ...BranchPlusPolicyFragment
    }
  }
  ${OFFER_FRAGMENT}
  ${BRANCH_PLUS_POLICY_FRAGMENT}
`;

export const GET_ACCOUNT_POLICY = gql`
  query getAccountPolicy($id: ID!, $accountId: ID!) {
    account: getAccount(id: $accountId) {
      id
      fname
      lname
      created
      mailingAddress {
        address
        address2
        city
        state
        zip
      }
      phoneNumber
      email
      inviter {
        id
        firstName
        lastName
      }
      frontTagId
      startedUploadingInventoryImages
      memberAffinityCode
      policies {
        ...PolicyFragment
      }
    }
    writeOffTransactions: getWriteOffTransactions(policyId: $id) {
      date
      amount
      paymentMethod
      paymentStatus
      description
    }
    billingDetails: getBillingDetails(accountId: $accountId, policyId: $id) {
      nextPaymentDate
      nextPaymentAmount
      nextPaymentTransactionFee
      nextPaymentTotal
      totalInstallments
      nextPayments {
        date
        remainingMonthly
        transactionFee
        total
      }
      remainingPayments
      activePaymentMethod {
        id
        brand
        last4
        bankName
        type
      }
      allPaymentMethods {
        id
        brand
        last4
        bankName
        expMonth
        expYear
        type
      }
      transactions {
        id
        isRefund
        paymentAmount
        paymentAmountFormatted
        paymentRefunded
        paymentRefundedFormatted
        refundedAt
        paymentDate
        paymentMethod
        paymentMethodBrand
        paymentMethodLast4
        paymentStatus
        internalDescription
        trigger
        failureReason
        isSweepPayment
      }
      refundTransactions {
        id
        isRefund
        paymentRefunded
        paymentRefundedFormatted
        refundedAt
        paymentMethod
        paymentMethodBrand
        paymentMethodLast4
        paymentStatus
        internalDescription
      }
      totalDue
      totalRemaining
      renewalPaymentDate
    }
    holdcards: getHoldCards(policyId: $id) {
      holdCardId
      createdBy
      createdDate
      reason
      deleted
      policyId
    }
    documents: getDocs(accountId: $accountId) {
      regular {
        path
        lastModified
      }
      internal {
        path
        lastModified
        displayName
        policyId
      }
    }
    inspection: getInspectionStatus(policyId: $id) {
      status
      inspectionId
      caseNumber
      dateOrdered
      dateCompleted
      completedType
    }
    violationsForRenewal: getViolationsForRenewal(policyId: $id) {
      policyId
      gotDrivingRecord
      autoViolationDates
      driverId
    }
    unsignedBixConversions: getUnsignedBixConversions(accountId: $accountId) {
      policyId
    }
    unsignedApplications: getUnsignedApplications(accountId: $accountId) {
      policyId
      applicationUrl
    }
    tasks: getOnboardingTasks(accountId: $accountId) {
      ... on OnboardingTasksResponse {
        success
        tasks {
          taskId
          taskName
          data
          completedDate
          policyId
          completedBy
          rejections {
            timestamp
            rejectedByUserId
            reason
          }
        }
      }
      ... on OnboardingTasksError {
        success
        error
      }
    }
    policyPaidAndPendingCash: getPolicyPaidAndPendingCash(policyId: $id) {
      currentTotalPaid
      pendingAmount
    }
  }
  ${fragments.PolicyFragment}
`;

export const GET_ACCOUNT_POLICY_PREVIEW = gql`
  query getAccountPolicyPreview($id: ID!, $accountId: ID!, $previewId: ID!) {
    account: getAccount(id: $accountId) {
      id
      fname
      lname
      created
      mailingAddress {
        address
        address2
        city
        state
        zip
      }
      phoneNumber
      email
      inviter {
        id
        firstName
        lastName
      }
      frontTagId
      startedUploadingInventoryImages
      memberAffinityCode
      policies {
        ...PolicyFragment
      }
    }
    policyPreview: getPolicyPreview(policyId: $id, previewId: $previewId) {
      ...PolicyPreviewResponse
    }
    writeOffTransactions: getWriteOffTransactions(policyId: $id) {
      date
      amount
      paymentMethod
      paymentStatus
      description
    }
    billingDetails: getBillingDetails(accountId: $accountId, policyId: $id) {
      nextPaymentDate
      nextPaymentAmount
      nextPaymentTransactionFee
      nextPaymentTotal
      totalInstallments
      nextPayments {
        date
        remainingMonthly
        transactionFee
        total
      }
      remainingPayments
      activePaymentMethod {
        id
        brand
        last4
        bankName
        type
      }
      allPaymentMethods {
        id
        brand
        last4
        bankName
        expMonth
        expYear
        type
      }
      transactions {
        id
        isRefund
        paymentAmount
        paymentAmountFormatted
        paymentRefunded
        paymentRefundedFormatted
        refundedAt
        paymentDate
        paymentMethod
        paymentMethodBrand
        paymentMethodLast4
        paymentStatus
        internalDescription
        trigger
        failureReason
        isSweepPayment
      }
      refundTransactions {
        id
        isRefund
        paymentRefunded
        paymentRefundedFormatted
        refundedAt
        paymentMethod
        paymentMethodBrand
        paymentMethodLast4
        paymentStatus
        internalDescription
      }
      totalDue
      totalRemaining
      renewalPaymentDate
    }
    holdcards: getHoldCards(policyId: $id) {
      holdCardId
      createdBy
      createdDate
      reason
      deleted
      policyId
    }
    documents: getDocs(accountId: $accountId) {
      regular {
        path
        lastModified
      }
      internal {
        path
        lastModified
        displayName
        policyId
      }
    }
    inspection: getInspectionStatus(policyId: $id) {
      status
      inspectionId
      caseNumber
      dateOrdered
      dateCompleted
      completedType
    }
    violationsForRenewal: getViolationsForRenewal(policyId: $id) {
      policyId
      gotDrivingRecord
      autoViolationDates
      driverId
    }
    unsignedBixConversions: getUnsignedBixConversions(accountId: $accountId) {
      policyId
    }
    unsignedApplications: getUnsignedApplications(accountId: $accountId) {
      policyId
      applicationUrl
    }
    tasks: getOnboardingTasks(accountId: $accountId) {
      ... on OnboardingTasksResponse {
        success
        tasks {
          taskId
          taskName
          data
          completedDate
          policyId
          completedBy
          rejections {
            timestamp
            rejectedByUserId
            reason
          }
        }
      }
      ... on OnboardingTasksError {
        success
        error
      }
    }
    policyPaidAndPendingCash: getPolicyPaidAndPendingCash(policyId: $id) {
      currentTotalPaid
      pendingAmount
    }
  }
  ${fragments.PolicyFragment}
  ${fragments.PolicyPreviewResponse}
`;

export const GET_TICKETS = gql`
  query ($params: GetTicketsParams!) {
    tickets: getTickets(params: $params) {
      frontTickets {
        items {
          date
          subject
          description
          url
        }
        nextFrontPageToken
        paginationEnabled
        morePages
      }
      sendGridEmails {
        id
        date
        status
        subject
        description
        url
        clicksCount
        opensCount
      }
    }
  }
`;

export const GET_FRONT_CONTACT = gql`
  query ($email: String!) {
    frontContactURL: getFrontContactURL(email: $email)
  }
`;

export const GET_ACCOUNT_CLAIMS = gql`
  query ($accountId: ID!) {
    claims: getAccountClaims(accountId: $accountId) {
      policyId
      claimNumber
      status
      TPA
      coverage
      cause
      lossDate
      claimType
      adjusterName
      adjusterEmail
      adjusterPhone
    }
  }
`;

export const GET_SNAPSHEET_EXPOSURE_DETAIL = gql`
  query ($accountId: ID!) {
    exposureDetails: getSnapsheetExposureDetail(accountId: $accountId) {
      occurrence
      claimId
      snapsheetPolicyId
      correctedPolicyId
      policyId
      accountId
      exposureNumber
      exposureName
      exposureType
      coverage
      lossDate
      claimType
      currentAdjusterName
      currentAdjusterPhone
      currentAdjusterEmail
      exposureStatus
      lossType
      vin
      vehicleMake
      vehicleModel
      vehicleYear
    }
  }
`;

export const GET_SNAPSHEET_NOTES_DETAIL = gql`
  query ($claimId: ID!) {
    notesDetail: getSnapsheetNotesDetail(claimId: $claimId) {
      dataSource
      primaryKeyId
      exposureId
      claimId
      userId
      userName
      body
      notesContactType
      notesCreatedAt
      notesExposureId
      notesNoteTemplateId
      notesNoteType
      notesProviderCode
      notesUpdatedAt
      notesVendorTaskId
      notesCreatedAtLocal
      emailsBcc
      emailsCc
      emailsConfigurableEmailTemplateId
      emailsContentType
      emailsCreatedAt
      emailsCreatedAtLocal
      emailsExposureId
      emailsFrom
      emailsIsOutgoing
      emailsNumberOfAttachments
      emailsOriginalTo
      emailsProviderCode
      emailsReferenceToken
      emailsStatus
      emailsSubject
      emailsTo
      emailsUpdatedAt
      smsConfigurableSmsTemplateId
      smsCountry
      smsCreatedAt
      smsCreatedAtLocal
      smsExposureId
      smsFrom
      smsIsOutgoing
      smsProviderCode
      smsStatus
      smsTo
      smsUpdatedAt
      createdAt
    }
  }
`;

export const SEND_EVENT_AS_MEMBER = gql`
  mutation sendEventAsMember($eventDetails: EventDetailsInput!) {
    sendEventAsMember(eventDetails: $eventDetails)
  }
  `;

export const COMPLETE_TASK = gql`
  mutation completeTask($taskDetails: TaskDetailsInput!, $ticket: CompleteTaskTicketInput) {
    completeTask(taskDetails: $taskDetails, ticket: $ticket) {
      ... on CompleteTask {
        success
      }
      ... on OnboardingTasksError {
        success
        error
      }
    }
  }
`;

export const APPROVE_INTERNAL_TASK = gql`
  mutation approveInternalTask($accountId: ID!, $taskId: String!) {
    approveInternalTask(accountId: $accountId, taskId: $taskId)
  }
`;

export const APPROVE_TASK = gql`
  mutation approveTask($taskDetails: ApproveTaskInput!) {
    approveTask(taskDetails: $taskDetails) {
      ... on ApproveTask {
        success
      }
      ... on OnboardingTasksError {
        success
        error
      }
    }
  }
`;

export const RESET_TASK = gql`
  mutation resetTask($taskDetails: TaskDetailsInput!) {
    resetTask(taskDetails: $taskDetails) {
      ... on ResetTask {
        success
      }
      ... on OnboardingTasksError {
        success
        error
      }
    }
  }
`;

export const CANCEL_TASK = gql`
  mutation cancelTask($taskDetails: TaskDetailsInput!) {
    cancelTask(taskDetails: $taskDetails) {
      ... on CancelTask {
        success
      }
      ... on OnboardingTasksError {
        success
        error
      }
    }
  }
`;

export const GET_DOCS_AND_TASKS = gql`
  query ($accountId: ID!) {
    unsignedApplications: getUnsignedApplications(accountId: $accountId) {
      policyId
      applicationUrl
    }
    documents: getDocs(accountId: $accountId) {
      regular {
        path
        lastModified
      }
      internal {
        path
        lastModified
        displayName
        policyId
      }
    }
    tasks: getOnboardingTasks(accountId: $accountId) {
      ... on OnboardingTasksResponse {
        success
        tasks {
          taskId
          taskName
          data
          completedDate
          policyId
          completedBy
          approvalDate
          approvedBy
          rejections {
            timestamp
            rejectedByUserId
            reason
          }
        }
      }
      ... on OnboardingTasksError {
        success
        error
      }
    }
  }
`;

export const GET_INTERNAL_TASKS = gql`
  query getInternalTasks($accountId: ID!) {
    internalTasks: getInternalTasks(accountId: $accountId) {
      ... on InternalTasksResponse {
        success
        tasks {
          accountId
          taskId
          taskName
          data
          bindState
          policyId
          createdDate
          approvalDate
          approvedBy
          completedDate
          completedBy
        }
      }
      ... on InternalTasksError {
        success
        error
      }
    }
  }
`;

export const GET_MEMBER_INVENTORY_IMAGES = gql`
  query GetMemberInventoryImages($accountId: ID!) {
    getMemberInventoryImages(accountId: $accountId) {
      images {
        key
        url
      }
    }
  }
`;
